import { useEffect, useState } from 'react';

export default function useGeoLocation() {
  const [locationData, setLocationData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getLocation();
  }, []);

  async function getLocation() {
    try {
      setLoading(true);
      setError(null);

      // Method 1: Try browser's native geolocation API first (most accurate)
      if (navigator.geolocation) {
        const position = await getCurrentPositionAsync();
        if (position) {
          // Reverse geocoding to get address details
          const addressData = await reverseGeocode(
            position.coords.latitude,
            position.coords.longitude
          );
          setLocationData({
            ...addressData,
            lat: position.coords.latitude,
            lon: position.coords.longitude,
            source: 'browser_geolocation',
          });
          setLoading(false);
          return;
        }
      }

      // Method 2: Fallback to IP-based geolocation
      const ipData = await getLocationByIP();
      if (ipData) {
        setLocationData({
          ...ipData,
          source: 'ip_api',
        });
        setLoading(false);
        return;
      }

      // Method 3: Final fallback - use a different IP service
      const fallbackData = await getLocationByIPFallback();
      if (fallbackData) {
        setLocationData({
          ...fallbackData,
          source: 'ipapi',
        });
        setLoading(false);
        return;
      }

      throw new Error('Unable to determine location');
    } catch (err) {
      console.error('Geolocation error:', err);
      setError(err.message);
      setLoading(false);
    }
  }

  // Promise wrapper for navigator.geolocation.getCurrentPosition
  function getCurrentPositionAsync() {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        (error) => {
          console.warn('Browser geolocation failed:', error.message);
          resolve(null); // Don't reject, just return null for fallback
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    });
  }

  // Reverse geocoding using a free service
  async function reverseGeocode(lat, lon) {
    try {
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`
      );

      if (response.ok) {
        const data = await response.json();
        return {
          city: data.city || data.locality,
          country: data.countryName,
          countryCode: data.countryCode,
          region: data.principalSubdivision,
          regionCode: data.principalSubdivisionCode,
          timezone: data.localityInfo?.administrative?.[0]?.timezone,
          zip: data.postcode,
        };
      }
    } catch (err) {
      console.warn('Reverse geocoding failed:', err);
    }

    // Fallback to basic lat/lon if reverse geocoding fails
    return {
      city: null,
      country: null,
      countryCode: null,
      region: null,
      regionCode: null,
      timezone: null,
      zip: null,
    };
  }

  // Primary IP-based geolocation
  async function getLocationByIP() {
    try {
      const response = await fetch('https://ipapi.co/json/');
      if (response.ok) {
        const data = await response.json();
        return {
          city: data.city,
          country: data.country_name,
          countryCode: data.country_code,
          lat: data.latitude,
          lon: data.longitude,
          region: data.region,
          regionCode: data.region_code,
          timezone: data.timezone,
          zip: data.postal,
        };
      }
    } catch (err) {
      console.warn('Primary IP geolocation failed:', err);
    }
    return null;
  }

  // Fallback IP-based geolocation
  async function getLocationByIPFallback() {
    try {
      const response = await fetch('https://ip-api.com/json/');
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          return {
            city: data.city,
            country: data.country,
            countryCode: data.countryCode,
            lat: data.lat,
            lon: data.lon,
            region: data.regionName,
            regionCode: data.region,
            timezone: data.timezone,
            zip: data.zip,
          };
        }
      }
    } catch (err) {
      console.warn('Fallback IP geolocation failed:', err);
    }
    return null;
  }

  return {
    city: locationData?.city,
    country: locationData?.country,
    countryCode: locationData?.countryCode,
    lat: locationData?.lat,
    lon: locationData?.lon,
    region: locationData?.region,
    regionCode: locationData?.regionCode,
    timezone: locationData?.timezone,
    zip: locationData?.zip,
    source: locationData?.source,
    loading,
    error,
    refetch: getLocation,
  };
}
